import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { forgotPassword } from "@/lib/auth-api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

type PageState = "idle" | "loading" | "sent";

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [pageState, setPageState] = useState<PageState>("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPageState("loading");
    await forgotPassword(email);
    setPageState("sent");
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <div className="flex items-center justify-between px-8 pt-6">
        <Link to="/login?tab=signin">
          <Logo />
        </Link>
        <LanguageSwitcher
          currentLang={i18n.language}
          onChange={(l) => void i18n.changeLanguage(l)}
          variant="compact"
        />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-8 pb-12">
        <div className="w-full max-w-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {t("forgotPassword.eyebrow")}
          </p>

          {pageState === "sent" ? (
            <div data-testid="forgot-password-sent">
              <h1 className="mt-2 font-display text-2xl font-bold text-brand-primary">
                {t("forgotPassword.successTitle")}
              </h1>
              <p className="mt-3 text-sm text-neutral-600">
                {t("forgotPassword.successBody", { email })}
              </p>
              <Link
                to="/login?tab=signin"
                className="mt-6 block text-center text-sm font-medium text-brand-primary underline hover:text-brand-primary/80"
              >
                {t("forgotPassword.backToLogin")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="mt-2 font-display text-2xl font-bold text-brand-primary">
                {t("forgotPassword.title")}
              </h1>
              <p className="mt-1.5 text-sm text-neutral-600">
                {t("forgotPassword.subtitle")}
              </p>

              <form onSubmit={(e) => void handleSubmit(e)} noValidate className="mt-6">
                <label
                  htmlFor="forgot-email"
                  className="mb-1 block text-xs font-medium text-neutral-700"
                >
                  {t("forgotPassword.emailLabel")}
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={pageState === "loading"}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-brand-primary placeholder-neutral-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-60"
                />

                <button
                  type="submit"
                  disabled={pageState === "loading" || !email}
                  className="mt-4 w-full rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:opacity-50"
                >
                  {pageState === "loading" ? t("forgotPassword.submitting") : t("forgotPassword.submit")}
                </button>
              </form>

              <Link
                to="/login?tab=signin"
                className="mt-4 block text-center text-sm font-medium text-neutral-600 underline hover:text-neutral-800"
              >
                {t("forgotPassword.backToLogin")}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
